import { SaveOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { Button, Col, Form, InputNumber, Row, Space, message } from 'antd';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import styles from './TrapImageInsectsVariationSettings.module.css';
import notificationTrapImageInsectsVariationSettingsApi from '../../../apis/NotificationTrapImageInsectsVariationSettingsApi';
import userApi from '../../../apis/UserApi';
import { NotificationTrapImageInsectsVariationSettings } from '../../../models/Entities';
import alertService from '../../../services/AlertService';

const TrapImageInsectsVariationSettings: React.FC = () => {
    /*** HOOKS ***/
    const intl = useIntl();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState<'initializing' | 'saving'>();
    const [settings, setSettings] = useState<NotificationTrapImageInsectsVariationSettings[]>([]);

    /*** EFFECTS ***/

    useEffect(() => {
        const init = async () => {
            try {
                setLoading('initializing');
                const user = await userApi.getCurrent();
                const settings = await notificationTrapImageInsectsVariationSettingsApi.list(user.companyId!);
                setSettings(settings);
                form.setFieldsValue(settings.reduce((acc, s, index) => ({ ...acc, [index]: s }), {}));
            } catch (error) {
                alertService.displayError(error, intl);
            } finally {
                setLoading(undefined);
            }
        };
        init();
    }, [form, intl]);

    /*** METHODS ***/

    const save = async (values: any) => {
        try {
            setLoading('saving');
            const updatedSettings = await Promise.all(
                settings.map((setting, index) => {
                    const updatedSetting: NotificationTrapImageInsectsVariationSettings = { ...setting, ...values[index] };
                    return setting.id
                        ? notificationTrapImageInsectsVariationSettingsApi.update(updatedSetting)
                        : notificationTrapImageInsectsVariationSettingsApi.create(updatedSetting);
                })
            );
            setSettings(updatedSettings);
            message.success(intl.formatMessage({ id: 'status.saved' }));
        } catch (error) {
            alertService.displayError(error, intl);
        } finally {
            setLoading(undefined);
        }
    };

    /*** VISUAL ***/

    return (
        <Form form={form} onFinish={save} colon={false} layout="vertical">
            {settings.map((setting, index) => (
                <Row gutter={[12, 0]} key={setting.id} className={styles.row}>
                    <Col span={24} lg={4}>
                        <Form.Item label={index === 0 ? ' ' : undefined}>
                            <strong>
                                <FormattedMessage id={`plague.type.${setting.plague}`} />
                            </strong>
                        </Form.Item>
                    </Col>
                    <Col span={24} lg={6}>
                        <Form.Item
                            label={index === 0 ? <FormattedMessage id="settings.insectsVariation.observationDays" /> : undefined}
                            name={[index, 'observationDays']}
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <InputNumber size="large" min={1} max={30} className={styles.inputNumber} />
                        </Form.Item>
                    </Col>
                    <Col span={24} lg={6}>
                        <Form.Item
                            label={index === 0 ? <FormattedMessage id="settings.insectsVariation.insectsVariation" /> : undefined}
                            name={[index, 'insectsVariation']}
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <InputNumber size="large" min={0} max={1000} className={styles.inputNumber} />
                        </Form.Item>
                    </Col>
                </Row>
            ))}
            <Row gutter={[24, 24]} className="buttons">
                <Col span={24}>
                    <Space>
                        <Link to="/">
                            <Button size="large" icon={<ArrowLeftOutlined />} />
                        </Link>
                        <Button type="primary" htmlType="submit" size="large" loading={loading === 'saving'} icon={<SaveOutlined />}>
                            <FormattedMessage id="button.save" tagName="span" />
                        </Button>
                    </Space>
                </Col>
            </Row>
        </Form>
    );
};
export default TrapImageInsectsVariationSettings;
